// Rules for the privacy tooling (docs/plans/golive-privacy-tooling.md §1–§3, §5.2).

import type { RecordsRequestKind, RecordsRequestStatus } from '../records/requests.ts';

// --- Audit viewer --------------------------------------------------------------------------

/** An action, or the start of one ending in a dot ("record."). Lower case, dots, underscores. */
export const AUDIT_ACTION_FILTER_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*\.?$/;

export type AuditActionFilter =
  | { readonly kind: 'exact'; readonly value: string }
  | { readonly kind: 'prefix'; readonly value: string };

/** "record." matches every record action; anything else matches one action exactly. */
export function auditActionFilter(action: string): AuditActionFilter {
  return action.endsWith('.')
    ? { kind: 'prefix', value: action }
    : { kind: 'exact', value: action };
}

// --- Break-glass review ----------------------------------------------------------------------

export type BreakGlassReviewErrorCode = 'privacy.own_break_glass' | 'privacy.already_reviewed';

/** Someone other than the person who broke the glass reviews it, once. */
export function breakGlassReviewError(facts: {
  actorId: string;
  reviewerId: string;
  reviewed: boolean;
}): BreakGlassReviewErrorCode | null {
  if (facts.reviewed) return 'privacy.already_reviewed';
  if (facts.actorId === facts.reviewerId) return 'privacy.own_break_glass';
  return null;
}

// --- Correction review -----------------------------------------------------------------------

export const CORRECTION_REVIEW_DECISIONS = ['accepted', 'declined'] as const;
export type CorrectionReviewDecision = (typeof CORRECTION_REVIEW_DECISIONS)[number];

export type CorrectionReviewErrorCode =
  | 'records.not_a_correction'
  | 'records.request_closed'
  | 'records.identity_not_verified'
  | 'records.already_reviewed';

/** A correction is reviewed once, after the identity check, while it is open. */
export function correctionReviewError(facts: {
  kind: RecordsRequestKind;
  status: RecordsRequestStatus;
  reviewed: boolean;
}): CorrectionReviewErrorCode | null {
  if (facts.kind !== 'correction') return 'records.not_a_correction';
  if (facts.status === 'closed') return 'records.request_closed';
  if (facts.status !== 'verified') return 'records.identity_not_verified';
  if (facts.reviewed) return 'records.already_reviewed';
  return null;
}

// --- Labelling a visit -----------------------------------------------------------------------

export const LABEL_CHANGES = ['apply', 'remove'] as const;
export type LabelChange = (typeof LABEL_CHANGES)[number];

export type LabelChangeErrorCode =
  'privacy.label_not_granted' | 'privacy.label_inactive' | 'privacy.label_unchanged';

/**
 * Whether a label may be put on or taken off a visit. A label is applied only when active
 * and when the person's role is cleared for it, so they keep sight of the visit they are
 * working on (plan P1). Removing needs nothing more: whoever can see the visit is already
 * cleared for every label on it.
 */
export function labelChangeError(facts: {
  change: LabelChange;
  code: string;
  visitLabels: readonly string[];
  granted: readonly string[];
  labelActive: boolean;
}): LabelChangeErrorCode | null {
  const carries = facts.visitLabels.includes(facts.code);
  if (facts.change === 'remove') return carries ? null : 'privacy.label_unchanged';
  if (carries) return 'privacy.label_unchanged';
  if (!facts.labelActive) return 'privacy.label_inactive';
  if (!facts.granted.includes(facts.code)) return 'privacy.label_not_granted';
  return null;
}
